import { useEffect, useState } from 'react';
import type { DealPlaceholder } from '@forecastify/shared';
import { Button, Dialog, Select, Stepper, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import type { DealFeeds } from './deal-kit';
import type { DealRecord } from './use-deal-record';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D6 · one placeholder: name, headcount, person.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One dialog for the row menu's three edits rather than three menu entries — they are one
 * `PATCH /placeholders/:id`, and only the fields that changed are sent.
 *
 * ⚠️ **DM-5 · a person on a placeholder is a tentative booking**, offered only while the workspace
 * allows it (`settings.tentativeBooking`). A placeholder that already names somebody still shows
 * them with the setting off, so the dialog never pretends the booking is not there.
 *
 * ⚠️ **A placeholder made from a person has no profile**, and taking the person off would leave it
 * neither — the server refuses it (`memberId`), so the dialog does not offer *nobody* there.
 */
export function PlaceholderDialog({
  placeholder,
  record,
  feeds,
  onClose,
}: {
  readonly placeholder: DealPlaceholder | null;
  readonly record: DealRecord;
  readonly feeds: DealFeeds;
  readonly onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [headcount, setHeadcount] = useState('1');
  const [memberId, setMemberId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (placeholder === null) return;
    setName(placeholder.name);
    setHeadcount(String(placeholder.headcount));
    setMemberId(placeholder.memberId ?? '');
    setError(null);
  }, [placeholder]);

  const tentative = feeds.config?.settings.tentativeBooking ?? false;
  const canPickPerson = tentative;
  const canClearPerson = placeholder?.profileId !== null;

  const submit = async (): Promise<void> => {
    if (placeholder === null) return;
    if (name.trim() === '') {
      setError(t('dealResource.edit.errorName'));
      return;
    }
    const count = Math.round(Number(headcount));
    const body: Record<string, unknown> = { version: placeholder.version };
    if (name.trim() !== placeholder.name) body.name = name.trim();
    if (Number.isFinite(count) && count >= 1 && count !== placeholder.headcount) {
      body.headcount = Math.min(50, count);
    }
    const nextMember = memberId === '' ? null : memberId;
    if (nextMember !== placeholder.memberId) body.memberId = nextMember;
    if (Object.keys(body).length === 1) {
      onClose();
      return;
    }
    setBusy(true);
    const refusal = await record.write(`/placeholders/${placeholder.id}`, 'PATCH', body);
    setBusy(false);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    onClose();
  };

  /* The current person stays pickable even if they left the member list or the setting is off. */
  const people = feeds.members.map((member) => ({ value: member.id, label: member.displayName }));
  if (
    placeholder?.memberId !== null &&
    placeholder?.memberId !== undefined &&
    !people.some((one) => one.value === placeholder.memberId)
  ) {
    people.unshift({ value: placeholder.memberId, label: placeholder.memberName ?? '' });
  }

  return (
    <Dialog
      open={placeholder !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('dealResource.row.edit')}
      description={t('dealResource.edit.hint')}
      closeLabel={t('common.close')}
      width={460}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealResource.edit.name')} required>
          <TextInput
            autoFocus
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        <Field label={t('dealResource.edit.headcount')} hint={t('dealResource.edit.headcountHint')}>
          <Stepper
            value={headcount}
            onChange={setHeadcount}
            min={1}
            max={50}
            step={1}
            aria-label={t('dealResource.edit.headcount')}
            decrementLabel={t('dealResource.edit.fewer')}
            incrementLabel={t('dealResource.edit.more')}
          />
        </Field>
        <Field
          label={t('dealResource.edit.person')}
          {...(!canPickPerson
            ? { note: t('dealResource.edit.personOff') }
            : !canClearPerson
              ? { note: t('dealResource.edit.personKeep') }
              : {})}
        >
          <Select
            searchable
            searchPlaceholder={t('dealResource.menu.search')}
            emptyLabel={t('dealResource.menu.empty')}
            value={memberId}
            disabled={!canPickPerson}
            onChange={(event) => {
              setMemberId(event.target.value);
              setError(null);
            }}
            options={[
              ...(canClearPerson ? [{ value: '', label: t('dealResource.edit.personNone') }] : []),
              ...people,
            ]}
          />
        </Field>

        {error === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-2">
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" disabled={busy} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
